import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';

import { Modal, PaginationArrow, SingleSelect } from '@/common';
import { Search } from '@/components/common';
import { Label, LABEL_TYPE } from '@/components/generic';
import { PAGE_SIZE } from '@/constants';
import { COLORS, VideoThemeType } from '@/enums';
import { calculateThemeDuration } from '@/helpers';
import { AssetItem } from '@/pages/Event/Asset/AssetItem';
import { CreateEditCustomTheme } from '@/pages/Event/TributeVideo/CreateVideo/CustomTheme/CreateEditCustomTheme';
import { PlusIcon } from '@/resources';
import { useGetAllCustomVideoTheme, useGetAllVideoTheme } from '@/services';
import { useGetClipsByVideoThemeIdQuery } from '@/services/videoThemeClips.service';
import { SelectOptionProps, ThemeProp, TributeVideoOptionsType, VideoTheme } from '@/types';

type ThemeComponentProps = {
  tributeVideoOptions?: TributeVideoOptionsType;
  setEnableNext: React.Dispatch<React.SetStateAction<boolean>>;
  setVideoTheme: React.Dispatch<React.SetStateAction<ThemeProp | null>>;
  videoTheme: ThemeProp | null;
  onSubStepChange?: (subStep: { label: string; isPending: boolean } | null) => void;
  subStepEnterRequestId?: number;
  subStepExitRequestId?: number;
  organizationId?: number;
};

export const ThemeComponent = ({
  setEnableNext,
  setVideoTheme,
  tributeVideoOptions,
  videoTheme,
  onSubStepChange,
  subStepEnterRequestId,
  subStepExitRequestId,
  organizationId
}: ThemeComponentProps) => {
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '200px 0px'
  });
  const [t] = useTranslation('common');
  const { eventId } = useParams();
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit'>('list');
  const [customThemeId, setCustomThemeId] = useState<number | null>(null);
  const [isCreateSubStepPending, setIsCreateSubStepPending] = useState<boolean>(false);
  const lastHandledEnterRequestId = useRef<number>(subStepEnterRequestId ?? 0);
  const lastHandledExitRequestId = useRef<number>(subStepExitRequestId ?? 0);

  const [search, setSearch] = useState<string>('');
  const [selectedThemeTypeFilter, setSelectedThemeTypeFilter] = useState<SelectOptionProps | null>(
    null
  );

  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);

  const themeTypeFilterOptions: SelectOptionProps[] = [
    { value: VideoThemeType.Public, label: t('public') },
    { value: VideoThemeType.Premium, label: t('premium') },
    { value: VideoThemeType.Organization, label: t('private') }
  ];

  const {
    data: allVideoThemes,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useGetAllVideoTheme(
    PAGE_SIZE * 2,
    search,
    organizationId,
    selectedThemeTypeFilter?.value as number
  );

  const { data: allCustomVideoThemes } = useGetAllCustomVideoTheme(
    0,
    Number(eventId),
    'Id',
    false,
    search
  );

  const { data: videoThemeClips } = useGetClipsByVideoThemeIdQuery(Number(videoTheme?.id));

  const customThemes: ThemeProp[] = useMemo(
    () =>
      allCustomVideoThemes?.pages.flatMap(
        (page) =>
          page?.data?.items?.map((theme: ThemeProp) => ({
            ...theme,
            themeSource: 'custom' as const,
            themeKey: `${theme.id}-custom`
          })) ?? []
      ) ?? [],
    [allCustomVideoThemes]
  );

  const publicThemes: ThemeProp[] = useMemo(
    () =>
      allVideoThemes?.pages.flatMap(
        (page) =>
          page?.data?.items?.map((theme: VideoTheme) => ({
            ...theme,
            themeSource: 'public' as const,
            themeKey: `${theme.id}-public`
          })) ?? []
      ) ?? [],
    [allVideoThemes]
  );

  const combinedThemes: ThemeProp[] = useMemo(
    () => (selectedThemeTypeFilter ? publicThemes : [...customThemes, ...publicThemes]),
    [customThemes, publicThemes, selectedThemeTypeFilter]
  );

  const getThemeKey = (theme: ThemeProp) =>
    `${theme.id}-${theme.themeSource ?? (theme.isPublish === undefined ? 'custom' : 'public')}`;
  const isSameTheme = (firstTheme?: ThemeProp | null, secondTheme?: ThemeProp | null) =>
    !!firstTheme && !!secondTheme && getThemeKey(firstTheme) === getThemeKey(secondTheme);

  useEffect(() => {
    setEnableNext(!!videoTheme);
  }, [videoTheme]);

  useEffect(() => {
    if (viewMode === 'create') {
      setEnableNext(false);
    }
  }, [viewMode]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (event.shiftKey) {
        event.preventDefault();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [inView]);

  const handleAssetSelect = (selectedTheme: ThemeProp) => {
    const updatedDuration = calculateThemeDuration(
      selectedTheme,
      tributeVideoOptions,
      videoThemeClips
    );
    setIsCreateSubStepPending(false);
    onSubStepChange?.(null);
    setVideoTheme({ ...selectedTheme, duration: updatedDuration });
  };

  const searchCallback = (query: string) => {
    setSearch(query);
  };

  const handleEditTheme = (theme: ThemeProp) => {
    setIsCreateSubStepPending(false);
    setCustomThemeId(theme.id);
    setViewMode('edit');
    onSubStepChange?.({ label: t('editCustomTheme'), isPending: false });
  };

  const handleCreateTheme = () => {
    setIsCreateSubStepPending(true);
    setCustomThemeId(null);
    setVideoTheme(null);
    onSubStepChange?.({ label: t('createCustomTheme'), isPending: true });
    setEnableNext(false);
  };

  const handleFinishCustomTheme = (newThemeResponse: ThemeProp | undefined) => {
    setIsCreateSubStepPending(false);
    setCustomThemeId(null);
    setViewMode('list');
    onSubStepChange?.(null);

    if (newThemeResponse?.id) {
      const newTheme = {
        ...newThemeResponse,
        themeSource: 'custom' as const,
        themeKey: `${newThemeResponse.id}-custom`
      } as ThemeProp;
      const updatedDuration = calculateThemeDuration(
        newTheme,
        tributeVideoOptions,
        videoThemeClips
      );
      setVideoTheme({ ...newTheme, duration: updatedDuration });
    }
  };
  useEffect(() => {
    if (
      !subStepEnterRequestId ||
      subStepEnterRequestId <= lastHandledEnterRequestId.current ||
      !isCreateSubStepPending ||
      viewMode !== 'list'
    ) {
      return;
    }

    lastHandledEnterRequestId.current = subStepEnterRequestId;
    setIsCreateSubStepPending(false);
    setViewMode('create');
    onSubStepChange?.({ label: t('createCustomTheme'), isPending: false });
  }, [subStepEnterRequestId, isCreateSubStepPending, viewMode, t]);

  useEffect(() => {
    if (
      !subStepExitRequestId ||
      subStepExitRequestId <= lastHandledExitRequestId.current ||
      (viewMode !== 'create' && viewMode !== 'edit')
    ) {
      return;
    }

    lastHandledExitRequestId.current = subStepExitRequestId;
    if (viewMode === 'create') {
      // Back from create substep: restore pending state so tile stays selected
      setIsCreateSubStepPending(true);
      setViewMode('list');
      onSubStepChange?.({ label: t('createCustomTheme'), isPending: true });
      setEnableNext(false);
    } else {
      // Back from edit substep: close substep and return to theme list
      handleFinishCustomTheme(undefined);
    }
  }, [subStepExitRequestId, viewMode]);

  return (
    <div className='flex size-full flex-col'>
      {viewMode === 'list' && (
        <>
          <div className='no-scrollbar mt-2 flex h-[50vh] flex-col overflow-auto 2xl:h-[60vh] 3xl:h-[65vh] 4xl:h-[65vh]'>
            <div className='my-1 flex w-full flex-col justify-between md:flex-row'>
              <div className='flex flex-col'>
                <Label text={t('theme')} type={LABEL_TYPE.H2} />
                <Label
                  text={t('pleaseChooseTheTheme')}
                  type={LABEL_TYPE.PARAGRAPH1}
                  className='mb-2 md:!font-semibold'
                />
              </div>
              <div className='flex w-full flex-row items-center justify-end gap-2 md:w-auto'>
                <div className='w-1/2 md:w-auto'>
                  <Search callback={searchCallback} />
                </div>
                <div className='w-1/2 md:w-auto'>
                  <SingleSelect
                    containerClassName='w-full md:w-36'
                    className='w-full md:w-36'
                    label=''
                    isClearable
                    id='themeTypeFilter'
                    placeholder={t('filters')}
                    options={themeTypeFilterOptions}
                    handleChange={(value) => setSelectedThemeTypeFilter(value as SelectOptionProps)}
                    value={selectedThemeTypeFilter}
                    textColor={COLORS.PRIMARY_BLUE}
                    borderStyle={`1px solid ${COLORS.PRIMARY_BLUE}`}
                    fontWeight='!font-bold'
                    dropdownIndicatorColor={COLORS.PRIMARY_BLUE}
                  />
                </div>
              </div>
            </div>

            <div className='grid grid-cols-2 gap-3 md:grid-cols-5 md:gap-4'>
              {combinedThemes.map((theme: ThemeProp, index: number, array: ThemeProp[]) => (
                <div key={theme.themeKey ?? getThemeKey(theme)}>
                  <AssetItem
                    setSelectedAsset={(selectedTheme) =>
                      handleAssetSelect(selectedTheme as ThemeProp)
                    }
                    setShowAsset={() => Promise.resolve()}
                    deleteSelectedAsset={() => Promise.resolve()}
                    asset={theme}
                    key={theme.themeKey ?? getThemeKey(theme)}
                    canBeDeleted={false}
                    canBeEdited={theme.isPublish === undefined && isSameTheme(videoTheme, theme)}
                    selectedAsset={videoTheme as ThemeProp}
                    selectedVideoUrl={''}
                    canBeSelected={!theme.isPremiumLocked}
                    editSelectedAsset={() => handleEditTheme(theme)}
                    canBePreviewed={!!theme.previewVideoUrl}
                    onPreviewClick={() => setPreviewVideoUrl(theme.previewVideoUrl ?? null)}
                    isTributeVideoAssetsPage={true}
                    isPremium={theme.type === VideoThemeType.Premium}
                    isPremiumLocked={theme.isPremiumLocked}
                  />
                  {index === array.length - 5 && <div ref={ref} className='h-px w-full' />}
                </div>
              ))}
              <div
                className={`flex aspect-square cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-4 text-center ${
                  isCreateSubStepPending
                    ? 'border-primary-blue bg-secondary-pale-blue-gray'
                    : 'border-blue-powder bg-primary-gray'
                }`}
                onClick={handleCreateTheme}>
                <PlusIcon className='size-9 text-primary-blue' />
                <Label
                  text={t('createCustomTheme')}
                  type={LABEL_TYPE.PARAGRAPH1}
                  className='mt-4 font-semibold !text-primary-blue'
                />
              </div>
            </div>
          </div>
          {(Boolean(hasNextPage) || isFetchingNextPage) && (
            <PaginationArrow isFetchingNextPage={isFetchingNextPage} inView={inView} />
          )}
        </>
      )}
      {(viewMode === 'create' || viewMode === 'edit') && (
        <CreateEditCustomTheme
          eventId={eventId}
          customThemeId={viewMode === 'edit' ? (customThemeId ?? undefined) : undefined}
          onDone={handleFinishCustomTheme}
        />
      )}
      <Modal
        isOpen={!!previewVideoUrl}
        onClose={() => setPreviewVideoUrl(null)}
        label={t('themePreview')}
        modalClasses='w-full h-auto max-w-[700px]'
        hasButton={false}
        hasCancelButton={false}
        height='h-auto'>
        {previewVideoUrl && (
          <video src={previewVideoUrl} controls autoPlay className='h-[400px] w-full rounded' />
        )}
      </Modal>
    </div>
  );
};
